import { ActionIcon, Flex, Menu, Stack, Text } from '@mantine/core';
import Tip from 'src/components/design-system/Tip';
import { IconDotsVertical, IconStar, IconStarFilled } from '@tabler/icons-react';
import { useState } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { DocumentVersion } from 'src/document-editor/hooks/useDocumentVersions';
import { formatVersionDate } from '../../utils/formatVersionDate';

type Props = {
  version: DocumentVersion;
  variant: 'primary' | 'secondary';
  isSelected?: boolean;
  onRemove?: () => void;
  onOverwrite?: () => void;
  onSelect?: () => void;
  onMakePrimary?: () => void;
  onDownloadPdf?: () => void;
};

export function DocumentVersionCell({
  version,
  variant,
  isSelected,
  onRemove,
  onOverwrite,
  onSelect,
  onMakePrimary,
  onDownloadPdf,
}: Props) {
  const [hovered, setHovered] = useState(false);
  const isPrimary = variant === 'primary';

  const showHighlight = isPrimary || isSelected || hovered;
  const borderColor = showHighlight
    ? 'var(--mantine-color-orange-4)'
    : 'var(--mantine-color-gray-2)';

  return (
    <Flex
      data-version-id={version.id}
      align="flex-start"
      gap="xs"
      p="xs"
      bg={isPrimary ? 'brand.0' : 'gray.0'}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onSelect}
      style={{
        borderRadius: 12,
        border: `1px solid ${borderColor}`,
        cursor: 'pointer',
      }}
    >
      <Stack gap={0} style={{ flex: 1 }}>
        <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10">
          V{version.versionNumber}: {formatVersionDate(version.createdAt)}
        </Text>
      </Stack>

      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <ActionIcon
            variant="subtle"
            size={16}
            c="gray.5"
            aria-label={`Version ${version.versionNumber} actions`}
            onClick={e => e.stopPropagation()}
          >
            <IconDotsVertical size={16} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown
          onMouseDown={e => e.stopPropagation()}
          onTouchStart={e => e.stopPropagation()}
        >
          {!isPrimary && (
            <Menu.Item
              onClick={e => {
                e.stopPropagation();
                onRemove?.();
              }}
            >
              Remove
            </Menu.Item>
          )}
          <Menu.Item
            onClick={e => {
              e.stopPropagation();
              onOverwrite?.();
            }}
          >
            Overwrite version
          </Menu.Item>
          <Menu.Item
            onClick={e => {
              e.stopPropagation();
              onDownloadPdf?.();
            }}
            disabled={!version.fileId}
          >
            Download document
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>

      {isPrimary ? (
        <IconStarFilled size={16} color="var(--mantine-color-warning-6)" />
      ) : (
        <Tip label="Set as primary" position="top">
          <ActionIcon
            variant="transparent"
            size={16}
            onClick={e => {
              e.stopPropagation();
              onMakePrimary?.();
            }}
          >
            <IconStar size={16} color="var(--mantine-color-gray-5)" />
          </ActionIcon>
        </Tip>
      )}
    </Flex>
  );
}
